import { useEffect, useState } from "react";
import axios from "axios";
import { ArrowLeft, ArrowRight, CheckCircle2 } from "lucide-react";
import BrandMark from "./BrandMark";
import { StatusPill } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE_URL = process.env.REACT_APP_SITE_URL;

const inputCls =
  "w-full rounded-md border border-white/12 bg-black/40 px-4 py-3 text-sm text-white placeholder-white/30 outline-none transition-colors duration-300 focus:border-cyan-300/60";

const Field = ({ label, id, optional, children }) => (
  <div>
    <label htmlFor={id} className="mb-2 block text-[11px] font-semibold uppercase tracking-[0.2em] text-white/70">
      {label}
      {optional && <span className="ml-2 normal-case tracking-normal text-white/50">(optional)</span>}
    </label>
    {children}
  </div>
);

export default function RegisterPage() {
  const empty = { first_name: "", last_name: "", email: "", display_name: "", marketing: false, consent: false, website: "" };
  const [form, setForm] = useState(empty);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const prevTitle = document.title;
    document.title = "Register for Early Access | AIROBOTCAMSTUDIO LTD";
    const descEl = document.head.querySelector('meta[name="description"]');
    const prevDesc = descEl ? descEl.getAttribute("content") : null;
    if (descEl) {
      descEl.setAttribute(
        "content",
        "Register for early access to AIROBOTCAMSTUDIO's Level 1 real-time AI video companion platform. MVP currently in development — no payment required."
      );
    }
    let canonical = document.head.querySelector('link[rel="canonical"]');
    const created = !canonical;
    const prevCanonical = created ? null : canonical.getAttribute("href");
    if (created) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", `${SITE_URL}/register`);
    window.scrollTo(0, 0);
    return () => {
      document.title = prevTitle;
      if (descEl && prevDesc) descEl.setAttribute("content", prevDesc);
      if (created) canonical.remove();
      else if (prevCanonical) canonical.setAttribute("href", prevCanonical);
    };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSending(true);
    try {
      await axios.post(`${API}/early-access`, form);
      setDone(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === "string" && detail
          ? detail
          : "Sorry, your registration could not be sent. Please try again or email us at info@airobotcamstudio.com."
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="register-page" className="min-h-screen bg-[#050505] text-white">
      <header className="sticky top-0 z-50 border-b border-white/10 bg-black/70 backdrop-blur-2xl">
        <div className="mx-auto flex h-[64px] max-w-4xl items-center justify-between px-6">
          <a
            href="/"
            data-testid="register-logo"
            className="flex items-center gap-2.5 font-display text-sm font-semibold tracking-[0.25em] text-white"
          >
            <BrandMark className="h-7 w-7 rounded-md" testid="register-logo-mark" />
            AIROBOTCAM<span className="text-cyan-300">STUDIO</span>
          </a>
          <a
            href="/"
            data-testid="register-back"
            className="flex shrink-0 items-center gap-2 text-[13px] font-medium text-cyan-200/70 transition-colors duration-300 hover:text-cyan-100"
          >
            <ArrowLeft size={15} />
            <span className="sm:hidden">Back</span>
            <span className="hidden sm:inline">Back to Home</span>
          </a>
        </div>
      </header>

      <main className="mx-auto max-w-xl px-6 py-16 lg:py-24">
        <div className="flex items-center gap-4">
          <BrandMark className="h-12 w-12 rounded-xl border border-white/10" testid="register-brand-logo" />
          <p className="font-display text-[11px] font-medium uppercase tracking-[0.35em] text-cyan-300/90">
            Early Access Registration
          </p>
        </div>

        <h1 className="font-display mt-7 text-3xl font-light leading-tight tracking-tighter text-white sm:text-4xl lg:text-5xl">
          Register for <span className="text-cyan-300">Early Access</span>
        </h1>
        <p className="mt-4 text-base leading-relaxed text-[#C5CBD5]">
          Register your interest in the AIROBOTCAMSTUDIO Level 1 platform — real-time AI video
          interaction with photorealistic virtual characters.
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <StatusPill tone="amber">MVP currently in development</StatusPill>
          <StatusPill tone="cyan">No payment required</StatusPill>
        </div>

        <p data-testid="register-honesty" className="mt-8 rounded-xl border border-white/10 bg-white/[0.03] px-6 py-5 text-xs leading-relaxed text-white/70">
          User accounts are not yet live — the Level 1 platform is currently in development. This
          is an early access registration: we will email you when registration opens. Registering
          is free and creates no payment obligation.
        </p>

        {done ? (
          <div data-testid="register-success" className="mt-10 rounded-2xl border border-cyan-300/25 bg-cyan-300/[0.05] p-8 text-center">
            <CheckCircle2 size={28} className="mx-auto text-cyan-300" />
            <p className="font-display mt-4 text-xl font-medium tracking-tight text-white">
              You're registered for early access
            </p>
            <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-[#C5CBD5]">
              Thank you — we'll email you when Level 1 registration opens. Nothing further is
              needed from you.
            </p>
            <a
              href="/"
              data-testid="register-success-home"
              className="group mt-7 inline-flex items-center gap-2 rounded-md border border-cyan-300/30 px-7 py-3.5 text-sm font-semibold text-cyan-200/90 transition-all duration-300 hover:-translate-y-0.5 hover:border-cyan-300/60 hover:text-cyan-100"
            >
              <ArrowLeft size={15} /> Back to Home
            </a>
          </div>
        ) : (
          <form data-testid="register-form" onSubmit={submit} className="mt-10 space-y-5">
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label="First Name" id="reg-first-name">
                <input
                  id="reg-first-name"
                  data-testid="register-first-name-input"
                  required
                  maxLength={80}
                  autoComplete="given-name"
                  className={inputCls}
                  value={form.first_name}
                  onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                  placeholder="First name"
                />
              </Field>
              <Field label="Last Name" id="reg-last-name">
                <input
                  id="reg-last-name"
                  data-testid="register-last-name-input"
                  required
                  maxLength={80}
                  autoComplete="family-name"
                  className={inputCls}
                  value={form.last_name}
                  onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                  placeholder="Last name"
                />
              </Field>
            </div>
            <Field label="Email" id="reg-email">
              <input
                id="reg-email"
                data-testid="register-email-input"
                required
                type="email"
                autoComplete="email"
                className={inputCls}
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="you@example.com"
              />
            </Field>
            <Field label="Display Name" id="reg-display-name" optional>
              <input
                id="reg-display-name"
                data-testid="register-display-name-input"
                maxLength={80}
                autoComplete="nickname"
                className={inputCls}
                value={form.display_name}
                onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                placeholder="How you'd like to appear (optional)"
              />
            </Field>

            <input
              type="text"
              name="website"
              data-testid="register-website-honeypot"
              value={form.website}
              onChange={(e) => setForm({ ...form, website: e.target.value })}
              className="hidden"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
            />

            <label className="flex cursor-pointer items-start gap-3 text-xs leading-relaxed text-[#C5CBD5]">
              <input
                type="checkbox"
                data-testid="register-consent-checkbox"
                required
                checked={form.consent}
                onChange={(e) => setForm({ ...form, consent: e.target.checked })}
                className="mt-0.5 h-4 w-4 shrink-0 accent-cyan-300"
              />
              <span>
                I have read and acknowledge the{" "}
                <a href="/privacy-policy" className="text-cyan-200/80 underline-offset-4 transition-colors hover:text-cyan-100 hover:underline">
                  Privacy Policy
                </a>{" "}
                and{" "}
                <a href="/terms-of-service" className="text-cyan-200/80 underline-offset-4 transition-colors hover:text-cyan-100 hover:underline">
                  Terms of Service
                </a>
                , and I consent to my details being stored so AIROBOTCAMSTUDIO can contact me about
                early access.
              </span>
            </label>

            <label className="flex cursor-pointer items-start gap-3 text-xs leading-relaxed text-[#C5CBD5]">
              <input
                type="checkbox"
                data-testid="register-marketing-checkbox"
                checked={form.marketing}
                onChange={(e) => setForm({ ...form, marketing: e.target.checked })}
                className="mt-0.5 h-4 w-4 shrink-0 accent-cyan-300"
              />
              <span>
                Optional: I'd also like occasional product news and updates. You can opt out at any
                time.
              </span>
            </label>

            {error && (
              <p data-testid="register-error" className="rounded-lg border border-red-400/30 bg-red-400/10 px-4 py-3 text-sm text-red-200">
                {error}
              </p>
            )}

            <button
              type="submit"
              data-testid="register-submit-btn"
              disabled={sending}
              className="group flex w-full items-center justify-center gap-2 rounded-md bg-cyan-300 px-7 py-4 text-sm font-bold uppercase tracking-[0.12em] text-black transition-transform duration-300 hover:-translate-y-0.5 hover:bg-cyan-200 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {sending ? "Registering…" : "Register for Early Access"}
              {!sending && <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />}
            </button>
          </form>
        )}
      </main>

      <footer className="border-t border-white/10">
        <div className="mx-auto flex max-w-4xl flex-wrap items-center justify-between gap-4 px-6 py-8">
          <p className="text-[11px] uppercase tracking-[0.2em] text-white/55">
            AIROBOTCAMSTUDIO LTD · UK Company No. 17410621
          </p>
          <a href="/" className="text-xs text-cyan-200/60 transition-colors hover:text-cyan-100">
            Back to Home
          </a>
        </div>
      </footer>
    </div>
  );
}
